import {invert} from 'lodash';

import {buildUrlToNavigateToCardInContactsManager} from '../../../../../helpers/contactsManagerHelpers';
import {computeIsGlobalNamespace, globalNamespace} from '../../../../../helpers/models/namespaceHelpers';
import {ActionWithPayload, batch} from '../../../../../helpers/reduxHelpers';
import {CardModel} from '../../../../../models/api/cardModel';
import {CustomFieldAttribute} from '../../../../../models/api/customFieldAttributeModel';
import {PreviewCardMergeModel} from '../../../../../models/api/previewCardMergeModel';
import {AppAction} from '../../../../appActions';
import {ThunkAction, ThunkAsyncAction} from '../../../../appThunk';
import {ResourceMap} from '../../../../appTypes';
import {
  fetchPreviewCardMergeAsyncAction,
  mergeCardsAsyncAction,
  OverrideMergeValues,
} from '../../../../data/cards/cardsActions';
import {
  getCustomFieldsForAccountsFlat,
  getCustomFieldsForCardsFlat,
} from '../../../../data/customFields/customFieldsSelectors';
import {getCards} from '../../../../data/dataSelectors';
import {getUserNamespace} from '../../../../data/teammates/teammatesSelectors';
import {navigateToLinkAction} from '../../../../stateless/navigationActions';
import {accountDetailsClearFetchedData} from '../../../accounts/accountDetailsPanelActions';
import {
  showContactsManagerCardsMergedSuccessBannerAction,
  showContactsManagerErrorBannerAction,
} from '../../../app/banners/bannersActions';
import {ContactsManagerErrorBannerTypesEnum} from '../../../app/banners/bannersState';
import {getFlatPanels} from '../../../panelsSelectors';
import {panelIsAccountDetailsPanel} from '../../../panelsState';
import {ContactsManagerPanelId} from '../../../panelStateBase';
import {getContactsManagerPanel} from '../contactsManagerPanelSelectors';
import {ContactsManagerParametersTypesEnum} from '../parameters/contactsManagerParameters';
import {contactsManagerTableForceRefreshAction} from '../table/contactsManagerTableActions';
import {ContactsManagerAccountDetailsAction} from './contactsManagerAccountDetailsActions';
import {sidePanelIsMergeContactsForm} from './contactsManagerSidePanelState';

/*
 * Type enum.
 */

export enum ContactsManagerSidePanelActionsEnum {
  OPEN_CREATE_CONTACT_FORM = '@@panels/contacts/contactsManager/sidePanel/OPEN_CREATE_CONTACT_FORM',
  OPEN_CREATE_ACCOUNT_FORM = '@@panels/contacts/contactsManager/sidePanel/OPEN_CREATE_ACCOUNT_FORM',
  OPEN_ACCOUNT_DETAILS = '@@panels/contacts/contactsManager/sidePanel/OPEN_ACCOUNT_DETAILS',
  OPEN_MERGE_CONTACTS = '@@panels/contacts/contactsManager/sidePanel/OPEN_MERGE_CONTACTS',

  PREVIEW_MERGE_CONTACTS = '@@panels/contacts/contactsManager/sidePanel/PREVIEW_MERGE_CONTACTS',
  PREVIEW_MERGE_CONTACTS_COMPLETED = '@@panels/contacts/contactsManager/sidePanel/PREVIEW_MERGE_CONTACTS_COMPLETED',
  PREVIEW_MERGE_CONTACTS_FAILED = '@@panels/contacts/contactsManager/sidePanel/PREVIEW_MERGE_CONTACTS_FAILED',

  CLOSE_SIDE_PANEL = '@@panels/contacts/contactsManager/sidePanel/CLOSE_SIDE_PANEL',
}

/*
 * Types.
 */

interface ContactsManagerSidePanelGenericPayload {
  panelId: ContactsManagerPanelId;
}

interface OpenCreateContactFormActionPayload extends ContactsManagerSidePanelGenericPayload {
  access: string;
  customAttributes: ReadonlyArray<CustomFieldAttribute>;
}

interface OpenCreateContactFormAction extends ActionWithPayload<OpenCreateContactFormActionPayload> {
  type: ContactsManagerSidePanelActionsEnum.OPEN_CREATE_CONTACT_FORM;
}

interface OpenCreateAccountFormActionPayload extends ContactsManagerSidePanelGenericPayload {
  customAttributes: ReadonlyArray<CustomFieldAttribute>;
}

interface OpenCreateAccountFormAction extends ActionWithPayload<OpenCreateAccountFormActionPayload> {
  type: ContactsManagerSidePanelActionsEnum.OPEN_CREATE_ACCOUNT_FORM;
}

interface OpenAccountDetailsAction extends ActionWithPayload<ContactsManagerSidePanelGenericPayload> {
  type: ContactsManagerSidePanelActionsEnum.OPEN_ACCOUNT_DETAILS;
}

interface CloseSidePanelAction extends ActionWithPayload<ContactsManagerSidePanelGenericPayload> {
  type: ContactsManagerSidePanelActionsEnum.CLOSE_SIDE_PANEL;
}

interface OpenMergeContactsActionPayload extends ContactsManagerSidePanelGenericPayload {
  cardIds: ReadonlyArray<number>;
}

interface OpenMergeContactsAction extends ActionWithPayload<OpenMergeContactsActionPayload> {
  type: ContactsManagerSidePanelActionsEnum.OPEN_MERGE_CONTACTS;
}

interface PreviewMergeContactsAction extends ActionWithPayload<ContactsManagerSidePanelGenericPayload> {
  type: ContactsManagerSidePanelActionsEnum.PREVIEW_MERGE_CONTACTS;
}

interface PreviewMergeContactsActionPayload extends ContactsManagerSidePanelGenericPayload {
  previewCardMerge: PreviewCardMergeModel;
}

interface PreviewMergeContactsCompletedAction extends ActionWithPayload<PreviewMergeContactsActionPayload> {
  type: ContactsManagerSidePanelActionsEnum.PREVIEW_MERGE_CONTACTS_COMPLETED;
}

interface PreviewMergeContactsFailedAction extends ActionWithPayload<ContactsManagerSidePanelGenericPayload> {
  type: ContactsManagerSidePanelActionsEnum.PREVIEW_MERGE_CONTACTS_FAILED;
}

/*
 * Action creators.
 */

function openCreateContactForm(
  panelId: ContactsManagerPanelId,
  access: string,
  customAttributes: ReadonlyArray<CustomFieldAttribute>,
): OpenCreateContactFormAction {
  return {
    type: ContactsManagerSidePanelActionsEnum.OPEN_CREATE_CONTACT_FORM,
    payload: {
      panelId,
      access,
      customAttributes,
    },
  };
}

function openCreateAccountForm(
  panelId: ContactsManagerPanelId,
  customAttributes: ReadonlyArray<CustomFieldAttribute>,
): OpenCreateAccountFormAction {
  return {
    type: ContactsManagerSidePanelActionsEnum.OPEN_CREATE_ACCOUNT_FORM,
    payload: {
      panelId,
      customAttributes,
    },
  };
}

function openAccountDetails(panelId: ContactsManagerPanelId): OpenAccountDetailsAction {
  return {
    type: ContactsManagerSidePanelActionsEnum.OPEN_ACCOUNT_DETAILS,
    payload: {
      panelId,
    },
  };
}

function openMergeContactsAction(
  panelId: ContactsManagerPanelId,
  cardIds: ReadonlyArray<number>,
): OpenMergeContactsAction {
  return {
    type: ContactsManagerSidePanelActionsEnum.OPEN_MERGE_CONTACTS,
    payload: {
      panelId,
      cardIds,
    },
  };
}

function closeSidePanel(panelId: ContactsManagerPanelId): CloseSidePanelAction {
  return {
    type: ContactsManagerSidePanelActionsEnum.CLOSE_SIDE_PANEL,
    payload: {
      panelId,
    },
  };
}

function previewMergeContactsAction(panelId: ContactsManagerPanelId): PreviewMergeContactsAction {
  return {
    type: ContactsManagerSidePanelActionsEnum.PREVIEW_MERGE_CONTACTS,
    payload: {
      panelId,
    },
  };
}

function previewMergeContactsCompletedAction(
  panelId: ContactsManagerPanelId,
  previewCardMerge: PreviewCardMergeModel,
): PreviewMergeContactsCompletedAction {
  return {
    type: ContactsManagerSidePanelActionsEnum.PREVIEW_MERGE_CONTACTS_COMPLETED,
    payload: {
      panelId,
      previewCardMerge,
    },
  };
}

function previewMergeContactsFailedAction(panelId: ContactsManagerPanelId): PreviewMergeContactsFailedAction {
  return {
    type: ContactsManagerSidePanelActionsEnum.PREVIEW_MERGE_CONTACTS_FAILED,
    payload: {
      panelId,
    },
  };
}

/*
 * Union type.
 */

export type ContactsManagerSidePanelAction =
  | OpenCreateContactFormAction
  | OpenCreateAccountFormAction
  | OpenAccountDetailsAction
  | OpenMergeContactsAction
  | CloseSidePanelAction
  | ContactsManagerAccountDetailsAction
  | PreviewMergeContactsAction
  | PreviewMergeContactsCompletedAction
  | PreviewMergeContactsFailedAction;

/*
 * Actions.
 */

export function openCreateContactFormAction(): ThunkAction {
  return (dispatch, getState) => {
    const contactsManagerPanel = getContactsManagerPanel(getState());
    if (!contactsManagerPanel) {
      return;
    }

    const {parameters} = contactsManagerPanel;
    const userNamespace = getUserNamespace(getState());

    // Fetch all the custom fields for Contacts and add a default value.
    const contactsCustomFields = getCustomFieldsForCardsFlat(getState());
    dispatch(
      openCreateContactForm(
        contactsManagerPanel.id,
        buildContactFormAccessFromParameterType(parameters.type, userNamespace),
        contactsCustomFields.map((field) => ({id: field.id, value: ''})),
      ),
    );
  };
}

export function openCreateAccountFormAction(): ThunkAction {
  return (dispatch, getState) => {
    const contactsManagerPanel = getContactsManagerPanel(getState());
    if (!contactsManagerPanel) {
      return;
    }

    // Fetch all the custom fields and add a default value.
    const accountsCustomFields = getCustomFieldsForAccountsFlat(getState());
    dispatch(
      openCreateAccountForm(
        contactsManagerPanel.id,
        accountsCustomFields.map((field) => ({id: field.id, value: ''})),
      ),
    );
  };
}

export function openAccountDetailsAction(): ThunkAction {
  return (dispatch, getState) => {
    const contactsManagerPanel = getContactsManagerPanel(getState());
    if (!contactsManagerPanel) {
      return;
    }

    // Check if we have an embedded account details panel, we may need to refresh
    const panels = getFlatPanels(getState()).filter(panelIsAccountDetailsPanel);
    const panelId = panels.find((p) => !p.popupAppPanelId)?.id;

    batch(() => {
      dispatch(openAccountDetails(contactsManagerPanel.id));
      if (panelId) {
        dispatch(accountDetailsClearFetchedData(panelId));
      }
    });
  };
}

export function openContactsManagerMergeContactsAsyncAction(
  cardIds: ReadonlyArray<number>,
): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const contactsManagerPanel = getContactsManagerPanel(getState());
    if (!contactsManagerPanel) {
      return;
    }

    dispatch(openMergeContactsAction(contactsManagerPanel.id, cardIds));

    await dispatch(mergeContactsPreviewMergeForCardIdsAsyncAction(contactsManagerPanel.id, cardIds));
  };
}

export function retryContactsManagerMergeContactsPreviewAsyncAction(): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const contactsManagerPanel = getContactsManagerPanel(getState());
    if (!contactsManagerPanel) {
      return;
    }

    if (
      !contactsManagerPanel.sidePanelState ||
      !sidePanelIsMergeContactsForm(contactsManagerPanel.sidePanelState)
    ) {
      return;
    }

    // When retrying we can use the card ids from the side panel state.
    await dispatch(
      mergeContactsPreviewMergeForCardIdsAsyncAction(
        contactsManagerPanel.id,
        contactsManagerPanel.sidePanelState.cardIds,
      ),
    );
  };
}

export function closeSidePanelAction(): ThunkAction {
  return (dispatch, getState) => {
    const contactsManagerPanel = getContactsManagerPanel(getState());
    if (!contactsManagerPanel) {
      return;
    }

    dispatch(closeSidePanel(contactsManagerPanel.id));
  };
}

export function mergeAndOpenCardDetailsContactsManagerAsyncAction(
  overrides?: OverrideMergeValues,
): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const contactsManagerPanel = getContactsManagerPanel(getState());
    if (!contactsManagerPanel) {
      return;
    }

    const {sidePanelState} = contactsManagerPanel;
    if (!sidePanelIsMergeContactsForm(sidePanelState)) {
      return;
    }
    const cards = getCards(getState());

    try {
      const mergedCard = await dispatch(
        mergeCardsAsyncAction(
          computeBaseCardIdForMerge(sidePanelState.cardIds, cards),
          sidePanelState.cardIds,
          overrides,
        ),
      );

      batch(() => {
        dispatch(closeSidePanelAction());
        dispatch(contactsManagerTableForceRefreshAction());
        dispatch(navigateToLinkAction(buildUrlToNavigateToCardInContactsManager(mergedCard)));
        dispatch(
          showContactsManagerCardsMergedSuccessBannerAction(
            contactsManagerPanel.id,
            sidePanelState.cardIds.length,
          ),
        );
      });
    } catch (error) {
      dispatch(
        showContactsManagerErrorBannerAction(
          contactsManagerPanel.id,
          ContactsManagerErrorBannerTypesEnum.MERGE_FAILED,
        ),
      );
    }
  };
}

/*
 * Type guards.
 */

const contactsManagerSidePanelActionsEnumMap = invert(ContactsManagerSidePanelActionsEnum);
export function actionIsContactsManagerSidePanelAction(
  action: AppAction,
): action is ContactsManagerSidePanelAction {
  return action.type in contactsManagerSidePanelActionsEnumMap;
}

/*
 * Helpers.
 */

function mergeContactsPreviewMergeForCardIdsAsyncAction(
  panelId: ContactsManagerPanelId,
  cardIds: ReadonlyArray<number>,
): ThunkAsyncAction {
  return async (dispatch, getState) => {
    try {
      const cards = getCards(getState());
      dispatch(previewMergeContactsAction(panelId));

      // We require a main card for all cards to be merged into.
      const previewMergeCard = await dispatch(
        fetchPreviewCardMergeAsyncAction(computeBaseCardIdForMerge(cardIds, cards), cardIds),
      );

      dispatch(previewMergeContactsCompletedAction(panelId, previewMergeCard));
    } catch (err) {
      dispatch(previewMergeContactsFailedAction(panelId));
    }
  };
}

function computeBaseCardIdForMerge(cardIds: ReadonlyArray<number>, cards: ResourceMap<CardModel>) {
  // Try to find a company card as the base card to merge into, otherwise we just use the first card.
  return (
    cardIds.find((cardId) => {
      const card = cards[cardId];
      if (!card) {
        return false;
      }
      return computeIsGlobalNamespace(card.namespace);
    }) ?? cardIds[0]
  );
}

function buildContactFormAccessFromParameterType(
  type: ContactsManagerParametersTypesEnum,
  userNamespace: string,
) {
  switch (type) {
    case ContactsManagerParametersTypesEnum.PRIVATE_CONTACTS:
      return userNamespace;
    default:
      return globalNamespace;
  }
}
